import { ReactNode, useMemo } from 'react';
import { InfoCard, Progress } from '@backstage/core-components';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  makeStyles,
} from '@material-ui/core';

import { PORTAL_CARD_TITLE_PROPS } from './cardTitle';

/**
 * Padrão compartilhado de estado assíncrono: cobre carregamento, erro e vazio
 * de forma consistente em todas as telas, substituindo as implementações
 * dispersas (`Progress`/`ResponseErrorPanel`/lista vazia silenciosa).
 *
 * Reaproveita componentes já usados no portal (`Progress`, `ResponseErrorPanel`,
 * `InfoCard` do `@backstage/core-components`) — não introduz biblioteca nova.
 *
 * Duas variantes:
 *
 * - `page` (padrão): ocupa a área de conteúdo da tela. Carregamento com
 *   `<Progress />` e vazio dentro de um `InfoCard`.
 * - `inline`: para uso **dentro** de um card já montado. Carregamento com
 *   `CircularProgress` pequeno ao lado do rótulo e vazio como texto — não
 *   aninha card dentro de card.
 */
export type AsyncStateProps<T> = {
  loading: boolean;
  /**
   * Aceita `string` porque a maior parte das telas guarda a mensagem de erro
   * como texto. Normalizado internamente antes de chegar ao painel de erro, o
   * que permite adotar o componente sem tocar na lógica de erro de cada tela.
   */
  error?: Error | string;
  data: T | undefined;
  /** Considera vazio; padrão: array de comprimento 0. */
  isEmpty?: (data: T) => boolean;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyAction?: ReactNode;
  /** Quando fornecido, exibe "Tentar novamente" no estado de erro. */
  onRetry?: () => void;
  /** Enquadramento do estado. Padrão: `page`. */
  variant?: 'page' | 'inline';
  /** Rótulo ao lado do carregamento na variante `inline`. */
  loadingLabel?: string;
  children: (data: T) => ReactNode;
};

function defaultIsEmpty<T>(data: T): boolean {
  return Array.isArray(data) && data.length === 0;
}

const useStyles = makeStyles(theme => ({
  inlineRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  inlineBlock: {
    marginTop: theme.spacing(1),
  },
  retry: {
    marginTop: theme.spacing(2),
  },
  inlineError: {
    color: theme.palette.error.main,
  },
}));

export function AsyncState<T>(props: AsyncStateProps<T>) {
  const {
    loading,
    error,
    data,
    isEmpty = defaultIsEmpty,
    emptyTitle = 'Nada para exibir',
    emptyDescription,
    emptyAction,
    onRetry,
    variant = 'page',
    loadingLabel = 'Carregando…',
    children,
  } = props;
  const classes = useStyles();

  const normalizedError = useMemo(
    () => (typeof error === 'string' ? new Error(error) : error),
    [error],
  );

  if (normalizedError) {
    if (variant === 'inline') {
      return (
        <Box>
          <Typography variant="body2" className={classes.inlineError}>
            {normalizedError.message}
          </Typography>
          {onRetry && (
            <Box className={classes.inlineBlock}>
              <Button variant="outlined" color="primary" onClick={onRetry}>
                Tentar novamente
              </Button>
            </Box>
          )}
        </Box>
      );
    }
    return (
      <Box>
        {/* Antes usava o `ResponseErrorPanel` do Backstage, que prefixa a
            mensagem com "Error:" e oferece um "stack trace" em inglês — num
            portal em pt-BR isso vazava jargão de desenvolvedor para quem só
            precisa saber o que falhou e tentar de novo. Aqui a mensagem da tela
            aparece como veio, dentro do mesmo `InfoCard` dos outros estados. */}
        <InfoCard
          title="Não foi possível carregar"
          titleTypographyProps={PORTAL_CARD_TITLE_PROPS}
        >
          <Typography variant="body2">{normalizedError.message}</Typography>
        </InfoCard>
        {onRetry && (
          <Box className={classes.retry}>
            <Button variant="outlined" color="primary" onClick={onRetry}>
              Tentar novamente
            </Button>
          </Box>
        )}
      </Box>
    );
  }

  if (loading || data === undefined) {
    if (variant === 'inline') {
      return (
        <Box className={classes.inlineRow}>
          <CircularProgress size={18} />
          <Typography variant="body2">{loadingLabel}</Typography>
        </Box>
      );
    }
    return <Progress />;
  }

  if (isEmpty(data)) {
    if (variant === 'inline') {
      return (
        <Box>
          <Typography variant="body2" color="textSecondary">
            {emptyTitle}
          </Typography>
          {emptyDescription && (
            <Typography variant="body2" color="textSecondary">
              {emptyDescription}
            </Typography>
          )}
          {emptyAction && (
            <Box className={classes.inlineBlock}>{emptyAction}</Box>
          )}
        </Box>
      );
    }
    return (
      <InfoCard
        title={emptyTitle}
        titleTypographyProps={PORTAL_CARD_TITLE_PROPS}
      >
        {emptyDescription && (
          <Typography variant="body2" color="textSecondary">
            {emptyDescription}
          </Typography>
        )}
        {emptyAction && (
          <Box className={classes.inlineBlock}>{emptyAction}</Box>
        )}
      </InfoCard>
    );
  }

  return <>{children(data)}</>;
}
